import React, { useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, Clock, Target, Filter, Zap, TrendingUp, ChevronDown, ChevronUp, Shield } from 'lucide-react';

const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Execution approvata</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button
            onClick={() => onApprove()}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4" /> Approva
          </button>
          <button
            onClick={onRegenerate}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" /> Rigenera
          </button>
          <button
            onClick={() => setShowRejectModal(true)}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <XCircle className="w-4 h-4" /> Rigetta
          </button>
        </div>
      </div>
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Spiega perché rigetti questa execution..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const EmptyState = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center text-gray-500">
      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
      <p className="text-sm">Clicca "Esegui Contract" per avviare</p>
    </div>
  </div>
);

const StringList = ({ items }) => {
  if (!items || items.length === 0) return <p className="text-gray-400 text-sm italic">Nessun elemento</p>;
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const PopulationBlock = ({ title, color, population }) => {
  const [showHO, setShowHO] = useState(false);
  if (!population) return null;

  const colorMap = {
    green: { wrap: 'bg-green-50 border-green-200', title: 'text-green-800', label: 'text-green-600', ho: 'bg-green-100/60 border-green-200' },
    red:   { wrap: 'bg-red-50 border-red-200',     title: 'text-red-800',   label: 'text-red-600',   ho: 'bg-red-100/60 border-red-200'   }
  };
  const c = colorMap[color] || colorMap.green;

  const hasHigherOrder = population.persuasion_awareness_profile
    || population.higher_order_qualification_signals?.length
    || population.higher_order_disqualification_risks?.length
    || population.autonomy_preservation_requirements?.length
    || population.transparent_selection_requirements?.length
    || population.higher_order_disqualification_signals?.length
    || population.autonomy_response_patterns?.length
    || population.commercial_intent_response_patterns?.length;

  // Green = desired, Red = undesired
  const isDesired = color === 'green';

  return (
    <div className={`border rounded-lg p-4 ${c.wrap}`}>
      <h4 className={`font-semibold mb-3 ${c.title}`}>{title}</h4>
      <div className="space-y-3">
        {population.dominant_traits?.length > 0 && (
          <div>
            <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${c.label}`}>Tratti dominanti</p>
            <StringList items={population.dominant_traits} />
          </div>
        )}
        {population.motivational_profile?.length > 0 && (
          <div>
            <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${c.label}`}>Profilo motivazionale</p>
            <StringList items={population.motivational_profile} />
          </div>
        )}
        {population.latent_priors?.length > 0 && (
          <div>
            <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${c.label}`}>Prior latenti</p>
            <StringList items={population.latent_priors} />
          </div>
        )}

        {hasHigherOrder && (
          <div className={`border rounded-lg p-3 mt-1 ${c.ho}`}>
            <button
              onClick={() => setShowHO(!showHO)}
              className={`flex items-center justify-between w-full text-xs font-semibold uppercase tracking-wide ${c.label}`}
            >
              <span>Higher-Order Profile</span>
              {showHO ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showHO && (
              <div className="mt-2 space-y-2">
                {population.persuasion_awareness_profile && (
                  <div>
                    <p className={`text-xs font-medium mb-0.5 ${c.label}`}>Persuasion Awareness</p>
                    <p className="text-xs text-gray-700">{population.persuasion_awareness_profile}</p>
                  </div>
                )}
                {isDesired && population.higher_order_qualification_signals?.length > 0 && (
                  <div>
                    <p className={`text-xs font-medium mb-0.5 ${c.label}`}>HO Qualification Signals</p>
                    <StringList items={population.higher_order_qualification_signals} />
                  </div>
                )}
                {isDesired && population.higher_order_disqualification_risks?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium mb-0.5 text-orange-600">HO Disqualification Risks</p>
                    <StringList items={population.higher_order_disqualification_risks} />
                  </div>
                )}
                {isDesired && population.autonomy_preservation_requirements?.length > 0 && (
                  <div>
                    <p className={`text-xs font-medium mb-0.5 ${c.label}`}>Autonomy Requirements</p>
                    <StringList items={population.autonomy_preservation_requirements} />
                  </div>
                )}
                {isDesired && population.transparent_selection_requirements?.length > 0 && (
                  <div>
                    <p className={`text-xs font-medium mb-0.5 ${c.label}`}>Transparent Selection</p>
                    <StringList items={population.transparent_selection_requirements} />
                  </div>
                )}
                {!isDesired && population.higher_order_disqualification_signals?.length > 0 && (
                  <div>
                    <p className={`text-xs font-medium mb-0.5 ${c.label}`}>HO Disqualification Signals</p>
                    <StringList items={population.higher_order_disqualification_signals} />
                  </div>
                )}
                {!isDesired && population.autonomy_response_patterns?.length > 0 && (
                  <div>
                    <p className={`text-xs font-medium mb-0.5 ${c.label}`}>Autonomy Response Patterns</p>
                    <StringList items={population.autonomy_response_patterns} />
                  </div>
                )}
                {!isDesired && population.commercial_intent_response_patterns?.length > 0 && (
                  <div>
                    <p className={`text-xs font-medium mb-0.5 ${c.label}`}>Commercial Intent Responses</p>
                    <StringList items={population.commercial_intent_response_patterns} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Contract015View = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  if (!execution) return <EmptyState />;

  const output = execution.output || execution.outputData || {};
  const cps = output.click_population_strategy || {};

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-800">Contract 1.5 — Click Population Strategy</h2>
          <p className="text-sm text-gray-500 mt-1">
            Strategia di auto-selezione del traffico che la creatività dovrà indurre.
          </p>
        </div>

        {/* Desired vs Undesired */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PopulationBlock
            title="Popolazione di Click Desiderata"
            color="green"
            population={cps.desired_click_population}
          />
          <PopulationBlock
            title="Popolazione di Click Indesiderata"
            color="red"
            population={cps.undesired_click_population}
          />
        </div>

        {/* Qualification Signals */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-green-600" />
            <h3 className="font-semibold text-gray-800">Segnali di Qualificazione</h3>
          </div>
          <StringList items={cps.qualification_signals} />
        </div>

        {/* Disqualification Signals */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-red-500" />
            <h3 className="font-semibold text-gray-800">Segnali di Disqualificazione</h3>
          </div>
          <StringList items={cps.disqualification_signals} />
        </div>

        {/* Click Separation Strategy */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-blue-600" />
            <h3 className="font-semibold text-gray-800">Click Separation Strategy</h3>
          </div>
          <StringList items={cps.click_separation_strategy} />
        </div>

        {/* Algorithmic Learning Objective */}
        {cps.algorithmic_learning_objective && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-blue-700" />
              <h3 className="font-semibold text-blue-800">Algorithmic Learning Objective</h3>
            </div>
            <p className="text-sm text-blue-700">{cps.algorithmic_learning_objective}</p>
          </div>
        )}

        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default Contract015View;
